if (!window.customElements.get('size-guide-modal')) {

  class SizeGuideModal extends HTMLElement {
    constructor() {
      super();
      this.gsap = null;
      this.dialog = null;
      this.tabbyComponent = null;
      this.accordionComponents = [];
      this.isInitialized = false;
      this.modalId = this.getAttribute('data-modal-id') || 'default';
    }

    connectedCallback() {
      this.waitForDependencies();
      this.handleUrlParams();
    }

    waitForDependencies() {
      whenReady('motion', (motion) => {
        this.motion = motion;
      });
      
      // Wait for dialog component to be ready
      this.waitForDialog();
    }

    waitForDialog() {
      const checkDialog = () => {
        this.dialog = this.querySelector('dialog-component');
        if (this.dialog && this.dialog.dialog) {
          this.initComponents();
          this.setupDialogListeners();
        } else {
          setTimeout(checkDialog, 100);
        }
      };
      checkDialog();
    }

    setupDialogListeners() {
      if (!this.dialog) return;
      
      // Listen for dialog close events to clear URL parameters
      this.dialog.addEventListener('a11y-dialog:hide', () => {
        this.clearUrlParams();
      });
    }

    initComponents() {
      if (this.isInitialized) return;
      
      // Find tabby component
      this.tabbyComponent = this.querySelector('tabby-component');
      
      // Find all accordion components
      this.accordionComponents = this.querySelectorAll('accordion-component');
      
      this.isInitialized = true;
    }

    handleUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const modalParam = urlParams.get('size-guide');
      const modalIdParam = urlParams.get('modal-id');
      const tabParam = urlParams.get('tab');
      const accordionParam = urlParams.get('accordion');
      
      // Only handle URL params if this modal matches the modal-id parameter (or if no modal-id specified, use default)
      if (modalParam === 'open' && (!modalIdParam || modalIdParam === this.modalId)) {
        // Delay to ensure components are ready, then apply selections before opening
        setTimeout(() => {
          this.openModal(tabParam, accordionParam);
        }, 500);
      }
    }

    /**
     * Open the modal with optional tab and accordion selections
     * @param {string} tabName - The name of the tab to open (optional)
     * @param {string} sectionName - The section name to open (optional)
     */
    openModal(tabName = null, sectionName = null) {
      if (!this.isInitialized) {
        setTimeout(() => this.openModal(tabName, sectionName), 100);
        return;
      }

      // Update tab selection first
      if (tabName) {
        this.selectTab(tabName);
      }
      
      // Small delay to ensure tab change is processed, then select accordion
      setTimeout(() => {
        if (sectionName) {
          this.selectAccordion(sectionName);
        }
        
        // Open the dialog after all selections are made
        if (this.dialog) {
          this.dialog.show();
          document.body.classList.add('modal-open');
        }
      }, 50);
    }

    /**
     * Select a specific tab by name using data-tab-name attribute
     * @param {string} tabName - The name of the tab to select
     */
    selectTab(tabName) {
      if (!this.tabbyComponent) return;

      // Find tab by data-tab-name attribute
      const tabElement = this.querySelector(`[data-tab-name="${tabName}"]`);
      
      if (tabElement) {
        tabElement.click();
      } else {
        console.warn(`Tab "${tabName}" not found in size guide modal. Make sure the tab has data-tab-name="${tabName}" attribute.`);
      }
    }

    /**
     * Select a specific accordion by section name
     * @param {string} sectionName - The section name to open (matches data-section-name attribute)
     */
    selectAccordion(sectionName) {
      if (!sectionName) return;

      // Find accordion in the active tab content
      const activeTabPanel = this.querySelector('[role="tabpanel"]:not([hidden])');
      if (activeTabPanel) {
        const accordion = activeTabPanel.querySelector('accordion-component');
        if (accordion && accordion.open) {
          // Find the section by data-section-name
          const section = activeTabPanel.querySelector(`[data-section-name="${sectionName}"]`);
          if (section) {
            // Get the index of this section within the accordion
            const sections = activeTabPanel.querySelectorAll('[data-section-name]');
            const index = Array.from(sections).indexOf(section);
            if (index !== -1) {
              // Open the specific accordion section
              accordion.open(index);
            }
          } else {
            console.warn(`Section "${sectionName}" not found in size guide modal. Make sure the section has data-section-name="${sectionName}" attribute.`);
          }
        }
      }
    }

    /**
     * Close the modal
     */
    closeModal() {
      if (this.dialog) {
        this.dialog.hide();
        document.body.classList.remove('modal-open');
      }
    }

    /**
     * Get the current modal state
     * @returns {Object} Object containing current tab and accordion state
     */
    getModalState() {
      const activeTab = this.querySelector('[role="tab"][aria-selected="true"], a[href^="#size-guide-tab-"][aria-selected="true"]');
      const activeTabPanel = this.querySelector('[role="tabpanel"]:not([hidden])');
      const activeAccordion = activeTabPanel?.querySelector('accordion-component');
      
      return {
        isOpen: this.dialog?.classList.contains('active') || false,
        activeTab: activeTab?.textContent?.trim() || null,
        activeAccordionSection: activeAccordion ? this.getActiveAccordionSection(activeAccordion) : null
      };
    }

    /**
     * Get the section name of the currently active accordion
     * @param {HTMLElement} accordion - The accordion component
     * @returns {string|null} The section name of the active accordion or null
     */
    getActiveAccordionSection(accordion) {
      const activePanel = accordion.querySelector('.ac-panel:not(.ac-panel--hidden)');
      if (!activePanel) return null;
      
      // Find the section that contains this panel
      const section = activePanel.closest('[data-section-name]');
      return section ? section.getAttribute('data-section-name') : null;
    }

    /**
     * Update URL with modal parameters
     * @param {string} tabName - The tab name to include in URL
     * @param {string} sectionName - The section name to include in URL
     */
    updateUrl(tabName = null, sectionName = null) {
      const url = new URL(window.location);
      
      // Always set modal to open
      url.searchParams.set('size-guide', 'open');
      
      // Set modal ID if not default
      if (this.modalId !== 'default') {
        url.searchParams.set('modal-id', this.modalId);
      } else {
        url.searchParams.delete('modal-id');
      }
      
      if (tabName) {
        url.searchParams.set('tab', tabName);
      } else {
        url.searchParams.delete('tab');
      }
      
      if (sectionName) {
        url.searchParams.set('accordion', sectionName);
      } else {
        url.searchParams.delete('accordion');
      }
      
      // Update URL without reloading the page
      window.history.replaceState({}, '', url);
    }

    /**
     * Clear URL parameters
     */
    clearUrlParams() {
      const url = new URL(window.location);
      url.searchParams.delete('size-guide');
      url.searchParams.delete('modal-id');
      url.searchParams.delete('tab');
      url.searchParams.delete('accordion');
      window.history.replaceState({}, '', url);
    }

    disconnectedCallback() {
      // Clean up if needed
    }
  }

  customElements.define('size-guide-modal', SizeGuideModal);
  
}

// Global utility function to open a specific size guide modal
window.openSizeGuideModal = function(tabName = null, sectionName = null, updateUrl = false, modalId = 'default') {
  const modal = document.querySelector(`size-guide-modal[data-modal-id="${modalId}"]`) || 
                document.querySelector('size-guide-modal');
  if (modal) {
    modal.openModal(tabName, sectionName);
    
    if (updateUrl) {
      modal.updateUrl(tabName, sectionName);
    }
  } else {
    console.warn(`Size guide modal with ID "${modalId}" not found on page`);
  }
};

// Global utility function to close a specific size guide modal
window.closeSizeGuideModal = function(clearUrl = true, modalId = 'default') {
  const modal = document.querySelector(`size-guide-modal[data-modal-id="${modalId}"]`) || 
                document.querySelector('size-guide-modal');
  if (modal) {
    modal.closeModal();
    document.body.classList.remove('modal-open');
    
    if (clearUrl) {
      modal.clearUrlParams();
    }
  }
};

// Global utility function to get modal state
window.getSizeGuideModalState = function(modalId = 'default') {
  const modal = document.querySelector(`size-guide-modal[data-modal-id="${modalId}"]`) || 
                document.querySelector('size-guide-modal');
  return modal ? modal.getModalState() : null;
};

// Global utility function to get all size guide modals
window.getAllSizeGuideModals = function() {
  return document.querySelectorAll('size-guide-modal');
};

// Global utility function to get a specific modal by ID
window.getSizeGuideModal = function(modalId = 'default') {
  return document.querySelector(`size-guide-modal[data-modal-id="${modalId}"]`) || 
         document.querySelector('size-guide-modal');
};
